<template>
  <div v-if="cart" class="cart-shell-container">
    <oie-action-wrapper>
      <template #left>
        <oie-article-search
          :name="listId"
          :use-gray-input="false"
          class="cart-shell-container__article-search"
          :ga-category="gaCategory"></oie-article-search>
      </template>
      <template #right>
        <oie-base-dropdown
          v-if="isSuperUser"
          v-model="cart.superUserPromoCode"
          :options="discountOptions"
          :aria-label="tStatic('selectPromo')"
          :label-text="tStatic('selectPromo')"
          container-classes="order-request__dropdown"
          class="cart-shell-container__dropdown"
          :default-selected="cart.superUserPromoCode"
          @input="selectPromo">
        </oie-base-dropdown>
      </template>
    </oie-action-wrapper>

    <oie-cart-products-table
      class="cart-shell-container__cart-products"
      :rows="cart.rows"
      :selected-rows.sync="selectedRowIds"
      enable-price-edit
      hide-file-upload
      @updateQuantity="updateQuantity"
      @updateComment="updateComment"
      @updateAssemblyNote="updateAssemblyNote"
      @updatePrice="updatePrice"
      @updateMachineOrOwner="updateMachineOrOwner"
      @updateMandatoryComment="updateMandatoryComment"
      @deleteRow="deleteRow"
      @markSelectedRows="markSelectedRows"
      @deleteSelectedRows="deleteSelectedRows"></oie-cart-products-table>

    <oie-additional-sales
      v-show="hasCartCrossSell"
      id="cart-cross-sell"
      class="cart-shell-container__product-carousel--first"
      :title="tStatic('cartCrossSellTitle')"
      :ga-list-type="listTypes.RELATED_CROSS_SELL"
      :products="cartCrossSell"></oie-additional-sales>

    <div v-if="hasDiscontinuedItems" class="cart-shell-container cart-shell-container__discontinuedItems">
      {{ tStatic('cartDiscontinuedItems') }} <strong>{{ cart.discontinuedItems }}</strong>
    </div>

    <div class="cart-shell-container__actions-container">
      <div class="cart-shell-container__actions-right">
        <ul v-if="hasCampaigns" class="cart-shell-container__campaigns-container">
          <li v-for="(campaign, i) in cart.campaigns" :key="`campaign_${i}`">
            <oie-campaign-chip :config="campaign"></oie-campaign-chip>
          </li>
        </ul>

        <div class="message-box message-box--green cart-shell-container__price-container">
          <template v-if="isSuperUser">
            <div class="cart-shell-container__summary cart-shell-container__summary--regular">
              <div>
                {{ tStatic('superUserShippingLabel') }}
              </div>

              <div>
                <div class="cart-shell-container__summary-value">
                  <span v-if="shippingValue">{{ shippingValue }}</span>

                  <button
                    v-if="!editShipping"
                    :aria-label="tStatic('editShipping')"
                    class="icon-button"
                    type="button"
                    @click="toggleEditShipping()">
                    <oie-use-svg svg="pen"></oie-use-svg>
                  </button>
                </div>

                <oie-cart-superuser-set-shipping v-if="editShipping" :shipping="cart.shippingOverride" @done-shipping="toggleEditShipping($event)"></oie-cart-superuser-set-shipping>
              </div>
            </div>

            <div class="cart-shell-container__summary cart-shell-container__summary--regular">
              <div>
                {{ tStatic('superUserCartTotalTb') }}
              </div>
              <div>
                {{ formatPrice(cart.totalTb) }}
              </div>
            </div>

            <div class="cart-shell-container__summary cart-shell-container__summary--regular">
              <div>
                {{ tStatic('superUserCartTotalTg') }}
              </div>
              <div>
                {{ `${cart.totalTg}%` }}
              </div>
            </div>
          </template>

          <div v-if="showDiscount" class="cart-shell-container__summary cart-shell-container__summary--discount">
            <div>
              {{ tStatic('orderDiscount') }}
            </div>
            <div>
              {{ formatPrice(cart.orderDiscount) }}
            </div>
          </div>

          <div class="cart-shell-container__summary">
            <div>
              {{ tStatic('cartSummaryTotal') }}
            </div>
            <div>
              {{ formatPrice(cart.totalPrice) }}
            </div>
          </div>
        </div>

        <oie-base-action
          v-if="hasRows && !useNewGuestCheckout"
          size="large"
          theme="secondary"
          data-pw="go-to-checkout-page"
          :url="clientModel.orderConfirmPageUrl">
          {{ tStatic('cartGotoCheckout') }}
        </oie-base-action>

        <!-- START Button and slidein for the temporary new guest checkout that will exist in vue3 later -->
        <!-- <oie-base-action
          v-if="hasRows && !useNewGuestCheckout"
          size="large"
          theme="secondary"
          data-pw="go-to-checkout-page"
          url="javascript:void(0)"
          @click="openGuestCheckoutSlideIn">
          {{ tStatic('cartGotoCheckout') }}
        </oie-base-action>
        <oie-guest-new-checkout-slide-in ref="guestCheckoutSlideIn"></oie-guest-new-checkout-slide-in> -->
        <!-- END Button and slidein for the temporary new guest checkout that will exist in vue3 later -->
      </div>
    </div>
    <oie-additional-sales
      v-show="hasGeneralCrossSell"
      id="general-cross-sell"
      class="cart-shell-container__product-carousel--second"
      :ga-list-type="listTypes.GENERAL_CROSS_SELL"
      :title="tStatic('generalCrossSellTitle')"
      :products="generalCrossSell"></oie-additional-sales>
  </div>
</template>

<script>
import httpService from '@/core/http-service'
import { mapGetters, mapActions } from 'vuex'
import cartService from '@/core/cart-service'
import _ from 'lodash'
import gtm from '@/core/enhanced-ecommerce-gtm'
import priceService from '@/core/price-service'
import { listTypes } from '@/core/analytics-variables'

export default {
  data() {
    return {
      selectedRowIds: [],
      discountOptions: [],
      editShipping: false,
      generalCrossSell: [],
      cartCrossSell: [],
      listTypes
    }
  },
  computed: {
    ...mapGetters('commerce', ['cart', 'numberOfItems']),
    ...mapGetters(['enableGtm']),
    ...mapGetters('ui', ['useVat']),
    listId() {
      return this.clientModel.cartId
    },
    gaCategory() {
      return listTypes.CART
    },
    hasRows() {
      return _.get(this.cart, 'rows.length', 0) > 0
    },
    cartRowCount() {
      return _.get(this.cart, 'rows.length', 0)
    },

    isSuperUser() {
      return (
        this.clientModel.impersonating !== null || this.clientModel.isSuperUser
      )
    },
    hasDiscontinuedItems() {
      return this.cart.discontinuedItems
    },
    selectedRowCount() {
      return this.selectedRowIds.length
    },
    selectedRowCountText() {
      return `${this.t('cartArticlesInList')} ${this.cartRowCount}`
    },
    showDiscount() {
      return this.cart.orderDiscount > 0
    },
    shippingValue() {
      if (this.cart.shippingOverride) {
        if (this.validateValue(this.cart.shippingOverride.absoluteCost)) {
          return this.formatPrice(this.cart.shippingOverride.absoluteCost)
        }

        if (this.validateValue(this.cart.shippingOverride.percentageOff)) {
          return `${this.cart.shippingOverride.percentageOff}%`
        }
      }

      return null
    },
    hasCampaigns() {
      return this.cart.campaigns && this.cart.campaigns.length > 0
    },
    generalCrossSellIds() {
      return _.map(this.generalCrossSell, (product) => product.id)
    },
    cartCrossSellIds() {
      return _.map(this.cartCrossSell, (product) => product.id)
    },
    hasGeneralCrossSell() {
      return this.generalCrossSell.length > 0
    },
    hasCartCrossSell() {
      return this.cartCrossSell.length > 0
    },
    useNewGuestCheckout() {
      // Temporary flag to show the new guest checkout in vue2. Should be removed when the new guest checkout is fully implemented in vue3
      return !this.clientModel.isAuthenticated && this.clientModel.enableCheckoutVue3
    }
  },
  watch: {
    numberOfItems() {
      this.getCrossSells()
    },
    useVat() {
      this.getCrossSells()
    }
  },
  mounted() {
    if (this.isSuperUser) {
      httpService.get('superUserCampaign/discount').then((response) => {
        this.discountOptions = response.data
      })
    }
  },
  methods: {
    ...mapActions('commerce', ['setCart', 'changeQuantity', 'getCart', 'deleteFromCart']),
    ...mapActions('ui', ['setActiveSlideIn']),
    openGuestCheckoutSlideIn() {
      this.$refs.guestCheckoutSlideIn.open()
    },
    selectPromo(selectedPromo) {
      httpService.post(`superUserCampaign/discount/${selectedPromo}`, null, { cartType: this.clientModel.cartType }).then((response) => {
        this.setCart(response.data)
      })
    },
    deleteSelectedRows() {
      const productsToRemove = _.filter(this.cart.rows, (item) => this.selectedRowIds.includes(item.id))
      const payload = _.map(this.selectedRowIds, (r) => { return { lineItemId: r } })
      this.$store.dispatch('commerce/deleteRowsFromCart', payload).then((response) => {
        if (this.enableGtm) {
          const gtmObject = _.map(productsToRemove, (product) => {
            return gtm.createGtmItem(
              product.itemId,
              product.displayName,
              null,
              product.purchasedOnCategory,
              this.formatPriceWithoutCurrency(product.priceExVat),
              product.quantity,
              this.gaCategory)
          })

          gtm.removeFromCart(gtmObject)
        }
        this.selectedRowIds = []
      })
    },
    toggleEditShipping() {
      this.editShipping = !this.editShipping
    },
    validateValue(value) {
      return !!((value !== null && parseInt(value) >= 0))
    },
    async getCrossSells() {
      this.getCrossSell('generalCrossSell', 'product/upsell')
      this.getCrossSell('cartCrossSell', 'product/cartCrossSell')
    },
    async getCrossSell(property, path) {
      this[property] = []
      const crossSell = await this.loadCrossSell(path)
      const ids = _.map(crossSell, (product) => product.id)
      await this.loadPrices(ids, crossSell)
      this[property] = crossSell
    },
    async loadCrossSell(path) {
      let response = await httpService.get(path)
      return response.data.data
    },
    async loadPrices(productIds, products) {
      const response = await httpService.post('productListPrices', { productId: productIds })
      priceService.populatePricesFromBackend(response.data, products)
    },
    updateQuantity({ row, quantity }) {
      const currentCartItem = this.cart.rows.find((item) => item.id === row)
      this.changeQuantity([{ cartName: 'Default', lineItemId: row, quantity }])
      if(this.enableGtm) {
        const gtmObject = gtm.createGtmItem(
          currentCartItem.itemId,
          currentCartItem.displayName,
          currentCartItem.item.purchasedToVehicle,
          currentCartItem.item.purchasedOnCategory,
          this.formatPriceWithoutCurrency(currentCartItem.priceExVat),
          0, // quantity
          listTypes.CART)

        if (quantity > currentCartItem.quantity) {
          gtmObject.quantity = quantity - currentCartItem.quantity
          gtm.addToCart(gtmObject)
        } else {
          gtmObject.quantity = currentCartItem.quantity - quantity
          gtm.removeFromCart(gtmObject)
        }
      }
    },
    async updateComment({ row, comment, callback }) {
      if(comment) {
        await cartService.saveNote(row, comment)
      } else {
        await cartService.deleteNote(row)
      }
      const item = this.cart.rows.find((item) => item.id === row)
      item.comment = comment
      callback.success()
    },
    async updateAssemblyNote({ row, comment, callback }) {
      if(comment) {
        await cartService.saveAssemblyNote(row, comment)
      } else {
        await cartService.deleteAssemblyNote(row)
      }
      const item = this.cart.rows.find((item) => item.id === row)
      item.comment = comment
      callback.success()
    },
    async updatePrice({ row, price }) {
      const { data } = await httpService.post('cart/setPrice', { newPrice: { lineItemId: row, overridenPrice: price } })
      this.setCart(data.data)
    },
    async updateMachineOrOwner({ row, machineOrOwner }) {
      const { data } = await cartService.markMachineForRow(machineOrOwner, row)
      this.setCart(data.data)
    },
    async updateMandatoryComment({ row, mandatoryComment }) {
      if(mandatoryComment) {
        await cartService.updateMandatoryComment(row, mandatoryComment)
        const item = this.cart.rows.find((item) => item.id === row)
        item.mandatoryComment = mandatoryComment
      }
    },
    async deleteRow({ row }) {
      const cartItem = this.cart.rows.find((item) => item.id === row)
      await this.deleteFromCart({ lineItemId: row })
      if (this.enableGtm) {
        const gtmObject = gtm.createGtmItem(
          cartItem.item.productId,
          cartItem.item.name,
          null,
          cartItem.item.purchasedOnCategory,
          this.formatPriceWithoutCurrency(cartItem.priceExVat),
          cartItem.quantity,
          null)

        gtm.removeFromCart(gtmObject)
      }
    },
    async markSelectedRows($event) {
      const { data } = await cartService.markMachineForRow($event, this.selectedRowIds)
      this.setCart(data.data)
    }
  }
}
</script>

<style lang="scss">
.cart-shell-container {
  margin-bottom: measure(half);
}

.cart-shell-container__article-search {
  margin-bottom: measure(double);
}

.cart-shell-container__message-container {
  margin-bottom: measure(full);
}

.cart-shell-container__table-information {
  @include body-semi-bold;
  margin-left: auto;
}

.cart-shell-container__cart-products {
  margin-bottom: measure(full);
}

.cart-shell-container__actions-container {
  display: flex;
  flex-direction: column;

  @include media(medium) {
    flex-direction: row;
    align-items: flex-end;
  }
}

.cart-shell-container__actions-left {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: measure(quarter);

  @include media(medium) {
    flex-direction: row;
    margin-bottom: 0;
  }
}

.cart-shell-container__actions-left > button {
  @include media(to-medium) {
    width: 100%;
  }
}

.cart-shell-container__current-list-action {
  background-color: transparent;

  &:hover {
    text-decoration: underline;
  }
}

.cart-shell-container__actions-right {
  width: 100%;

  @include media(medium) {
    max-width: 24rem;
    width: 100%;
    margin-left: auto;
  }
}

.cart-shell-container__campaigns-container {
  @include reset-list;
  margin-bottom: measure(half);
}

.cart-shell-container__campaigns-container>*:not(:last-child) {
  margin-bottom: measure(eighth);
}

.cart-shell-container__action-button {
  width: 100%;
}

.cart-shell-container__price-container {
  margin-bottom: measure(quarter);
  padding: measure(quarter);
}

.cart-shell-container__price-container > *:not(:last-child) {
  margin-bottom: measure(quarter);
}

.cart-shell-container__summary {
  @include h3-semi-bold;
  display: flex;
  justify-content: space-between;
  line-height: 1;
}

.cart-shell-container__summary--regular {
  @include body;
}

.cart-shell-container__summary--discount {
  @include body;
  color: color(text-red);
}

.cart-shell-container__summary > div:last-child {
  margin-left: measure(quarter);

  @include media(medium) {
    margin-left: auto;
  }
}

.cart-shell-container__discontinuedItems {
  margin-top: measure(neghalf);
  margin-bottom: measure(quarter);
}

.cart-shell-container__dropdown {
  width: 100%;

  @include media(to-small) {
    order: 1;
    max-width: initial;
    margin-bottom: measure(half);
  }

  @include media(small) {
    margin-left: auto;
  }
}

.cart-shell-container__summary-value {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: measure(quarter);
}

//This code sucks a bit. Should be refactored
.cart-shell-container__product-carousel--first {
  margin-bottom: measure(full);
}

.cart-shell-container__product-carousel--second {
  margin-top: measure(full);
}
</style>
